import React, { useEffect } from "react";
import UI from "@mile-meeting/mile-ui";
import { PolicyCustomizeSection } from "uis/Section";
import { ContentContainer, SyncWorkspaceInfoContainer, TitleContainer } from "./SlackSyncSection.styles";
import AlertModal from "uis/modal/alert-modal";
import { openSlackPopup } from "pages/setting/service-connect-manage/slack-oauth-popup";
import { disconnectSlack, syncSlack } from "apis/messenger-auto-sync";
import { useToast } from "hook/useToast";

type SlackSyncSectionProps = {
  isSynced: boolean;
  syncedWorkspaceName: string;
  onUpdateIntegration?: () => void;
};

const SlackSyncSection: React.FC<SlackSyncSectionProps> = ({ isSynced, syncedWorkspaceName, onUpdateIntegration }) => {
  const [showActivateModal, setShowActivateModal] = React.useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = React.useState(false);

  const { showToast } = useToast();

  const onOpenSlackSyncModal = () => {
    setShowActivateModal(true);
  };

  const onOpenSlackUnlinkModal = () => {
    setShowDeactivateModal(true);
  };

  const onHandleSync = () => {
    //* 일괄 연동 자동화하기 버튼 클릭시
    openSlackPopup(async (botToken, userId, teamName, workspaceId) => {
      const result = await syncSlack(workspaceId, teamName, botToken);
      if (!result) {
        showToast("Slack 일괄 연동 자동화 활성화 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
        return;
      }
      showToast("Slack 일괄 연동 자동화가 활성화되었습니다.", "success");
      onUpdateIntegration?.();
      setShowActivateModal(false);
    });
  };

  const onHandleDeactivate = async () => {
    const result = await disconnectSlack();
    if (!result) {
      showToast("Slack 일괄 연동 자동화 비활성화 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
      return;
    }
    if (result) {
      showToast("Slack 일괄 연동 자동화가 비활성화되었습니다.", "success");
      onUpdateIntegration?.();
      setShowDeactivateModal(false);
    }
  };

  return (
    <PolicyCustomizeSection icon={<UI.Icon name="slack" size={20} />} title="Slack 일괄 연동">
      <ContentContainer>
        <TitleContainer>
          <UI.Typography type="body01 normal" fontStyle="bold">
            Slack 일괄 연동 자동화
          </UI.Typography>
          <UI.Typography type="body02 normal" color="var(--alias-color-label-alternative)">
            활성화 시 Mile의 워크스페이스와 Slack의 워크스페이스 간 멤버 대조를 통해 연동을 일괄적으로 진행하며,
            <br />일 1회 자동 대조 및 추가적 연동을 진행합니다.
          </UI.Typography>
        </TitleContainer>
        <UI.Selection.Toggle.Default
          state={isSynced}
          size="large"
          onChange={() => {
            if (isSynced) {
              onOpenSlackUnlinkModal();
            } else {
              onOpenSlackSyncModal();
            }
          }}
        />
      </ContentContainer>
      {isSynced && (
        <SyncWorkspaceInfoContainer>
          <UI.Typography type="body01 normal" color="var(--alias-color-label-normal)">
            연동된 Slack 워크스페이스
          </UI.Typography>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <UI.Icon name="link" size={16} color="var(--alias-color-label-neutral)" />
            <UI.Typography type="body02 normal" fontStyle="regular" color="var(--alias-color-label-neutral)">
              {syncedWorkspaceName}
            </UI.Typography>
          </div>
        </SyncWorkspaceInfoContainer>
      )}
      <AlertModal
        modalIsOpen={showActivateModal}
        title={
          <p>
            Slack 일괄 연동 자동화를
            <br />
            활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            Mile과 Slack 일괄 연동 및<br />
            주기적 연동 자동화가 이루어집니다.
            <br />
            <span style={{ color: "var(--alias-color-status-negative)" }}>
              (기존에 Slack을 개별 연동한 멤버들은
              <br />
              해제 후 재연동됩니다.)
            </span>
          </p>
        }
        btnTitle="일괄 연동 자동화하기"
        closeModal={() => setShowActivateModal(false)}
        onChange={onHandleSync}
      />
      <AlertModal
        onChange={onHandleDeactivate}
        closeModal={() => setShowDeactivateModal(false)}
        btnTitle="일괄 연동 자동화 비활성화하기"
        modalIsOpen={showDeactivateModal}
        title={
          <p style={{ textAlign: "center" }}>
            Slack 일괄 연동 자동화를
            <br />
            비활성화하시겠습니까?
          </p>
        }
        content={
          <p style={{ textAlign: "center" }}>
            연동이 완료된 멤버들의 연동 상태는 유지되며,
            <br />
            주기적 자동화가 중지됩니다.
          </p>
        }
      />
    </PolicyCustomizeSection>
  );
};

export default React.memo(SlackSyncSection);
